import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { APP_ENVIRONMENT } from '../tokens/app-environment.token';

/**
 * Guard that restricts access based on allowed environments.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'protected',
 *   canActivate: [environmentAccessGuard],
 *   data: {
 *     allowedEnvironments: ['development', 'staging'],
 *     redirectUrl: '/dashboard' // optional, defaults to portal-specific dashboard
 *   },
 *   component: MyComponent
 * }
 * ```
 *
 * If `allowedEnvironments` is not provided or is an empty array, access is DENIED.
 * The current environment name is checked against this list.
 * If not allowed, user is redirected to `redirectUrl` or portal-specific dashboard.
 */
export const environmentAccessGuard: CanActivateFn = (route, state) => {
  const environment = inject(APP_ENVIRONMENT);
  const router = inject(Router);

  // Get allowed environments from route data
  const allowedEnvironments = route.data['allowedEnvironments'] as
    | string[]
    | undefined;

  // If no environments are specified, deny access
  if (!allowedEnvironments || allowedEnvironments.length === 0) {
    const redirectUrl =
      route.data['redirectUrl'] || `/${environment['portal']}/dashboard`;
    router.navigateByUrl(redirectUrl);
    return false;
  }

  const currentEnvironment = environment['environmentName'] as string;

  // Check if current environment is in allowed list
  if (allowedEnvironments.includes(currentEnvironment)) {
    return true;
  }

  // Access denied, redirect to dashboard
  const redirectUrl =
    route.data['redirectUrl'] || `/${environment['portal']}/dashboard`;
  router.navigateByUrl(redirectUrl);
  return false;
};
